import { useEffect, useMemo, useState } from 'react';
import { getSalesManagerL1List, getSalesPersonList } from '../../Service/ApiService';
import { showAlert } from 'utils/ErrorMessage';

const buildUserFullName = (user) => {
  const displayName = String(user?.displayName || '').trim();
  const userCode = String(user?.userCode || '').trim();

  const removeCodeFromName = (name, code) => {
    if (!name || !code) {
      return name;
    }

    return name
      .replace(new RegExp(`^${code}[\\s-]*`, 'i'), '')
      .replace(new RegExp(`[\\s-]*${code}$`, 'i'), '')
      .trim();
  };

  if (displayName) {
    return removeCodeFromName(displayName, userCode);
  }

  const fallbackName = [user?.firstName, user?.lastName]
    .filter((value) => String(value || '').trim())
    .join(' ');

  return removeCodeFromName(fallbackName, userCode);
};

const buildUserOption = (user) => {
  const userCode = String(user?.userCode || '').trim();
  const fullName = String(buildUserFullName(user) || '').trim();

  return {
    value: userCode,
    label: [fullName, userCode].filter(Boolean).join(' - ')
  };
};

const getTourExpenseRoleCode = (user = {}) => {
  const normalizedUserTypeId = String(user?.userTypeId || '').trim().toLowerCase();

  if (normalizedUserTypeId === 'ag' || normalizedUserTypeId === 'agent') {
    return 'AG';
  }

  const normalizedDesignation = Array.isArray(user?.designation)
    ? user.designation.map((value) => String(value || '').trim().toLowerCase()).join(' ')
    : JSON.stringify(user?.designation || '').toLowerCase();

  if (normalizedDesignation.includes('area sales officer')) {
    return 'SO';
  }

  if (normalizedUserTypeId === 'prim') {
    return 'SP';
  }

  return 'SP';
};

const TourExpenseClaimController = (initialClaims = []) => {
  const loggedInUser = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const apiUserCode = String(loggedInUser?.userCode || '').trim();
  const loggedInUserCode = String(apiUserCode || '').trim().toUpperCase();
  const roleCode = getTourExpenseRoleCode(loggedInUser);
  const [salespersonCodeOptions, setSalespersonCodeOptions] = useState([]);
  const [salespersonL1Options, setSalespersonL1Options] = useState([]);
  const [salespersonSearchText, setSalespersonSearchText] = useState('');
  const [salespersonL1SearchText, setSalespersonL1SearchText] = useState('');
  const [salespersonLoading, setSalespersonLoading] = useState(true);
  const [salespersonL1Loading, setSalespersonL1Loading] = useState(true);
  const [isSalespersonUser, setIsSalespersonUser] = useState(false);
  const [isSalespersonL1User, setIsSalespersonL1User] = useState(false);
  const [claims, setClaims] = useState(initialClaims);
  const optionsLoading = salespersonLoading || salespersonL1Loading;
  const isAgentUser = roleCode === 'AG';
  const isPrimUser = roleCode === 'PRIM';
  const isSOUser = roleCode === 'SO';
  const isSPUser = roleCode === 'SP';
  const isApprovalUser = !isAgentUser;

  const approvalPendingClaims = useMemo(
    () => claims,
    [claims]
  );

  const agentVisibleClaims = useMemo(
    () => claims,
    [claims]
  );

  const visibleClaims = isApprovalUser ? approvalPendingClaims : agentVisibleClaims;

  useEffect(() => {
    let isMounted = true;

    const trimmedLoggedInUserCode = loggedInUserCode.trim();

    const loadApprovalRoleOptions = async () => {
      if (!trimmedLoggedInUserCode) {
        if (isMounted) {
          setSalespersonLoading(false);
          setSalespersonL1Loading(false);
        }
        return;
      }

      try {
        const [salespersonResponse, salesManagerResponse] = await Promise.all([
          getSalesPersonList(trimmedLoggedInUserCode, apiUserCode),
          getSalesManagerL1List(trimmedLoggedInUserCode, apiUserCode)
        ]);

        if (!isMounted) {
          return;
        }

        const salespersonRoleOptions = Array.isArray(salespersonResponse?.salesPersonList)
          ? salespersonResponse.salesPersonList.map(buildUserOption).filter((option) => option.value)
          : [];
        const salesManagerRoleOptions = Array.isArray(salesManagerResponse?.userLevelList)
          ? salesManagerResponse.userLevelList.map(buildUserOption).filter((option) => option.value)
          : [];

        setIsSalespersonUser(
          salespersonRoleOptions.some((option) => option.value.toUpperCase() === trimmedLoggedInUserCode)
        );
        setIsSalespersonL1User(
          salesManagerRoleOptions.some((option) => option.value.toUpperCase() === trimmedLoggedInUserCode)
        );
      } catch (error) {
        if (isMounted) {
          setIsSalespersonUser(false);
          setIsSalespersonL1User(false);
          showAlert(error?.message || 'Failed to load approval role data.');
        }
      } finally {
        if (isMounted) {
          setSalespersonLoading(false);
          setSalespersonL1Loading(false);
        }
      }
    };

    loadApprovalRoleOptions();

    return () => {
      isMounted = false;
    };
  }, [apiUserCode, loggedInUserCode]);

  useEffect(() => {
    let isMounted = true;
    const trimmedSearchText = salespersonSearchText.trim();

    const loadSalespersonOptions = async () => {
      if (!isAgentUser && !isSOUser) {
        if (isMounted) {
          setSalespersonCodeOptions([]);
          setSalespersonLoading(false);
        }
        return;
      }

      if (trimmedSearchText && trimmedSearchText.length < 3) {
        if (isMounted) {
          setSalespersonLoading(false);
        }
        return;
      }

      setSalespersonLoading(true);

      try {
        const salespersonResponse = await getSalesPersonList(trimmedSearchText, apiUserCode);

        if (!isMounted) {
          return;
        }

        setSalespersonCodeOptions(
          Array.isArray(salespersonResponse?.salesPersonList)
            ? salespersonResponse.salesPersonList.map(buildUserOption).filter((option) => option.value)
            : []
        );
      } catch (error) {
        if (isMounted) {
          setSalespersonCodeOptions([]);
          showAlert(error?.message || 'Failed to load salesperson dropdown data.');
        }
      } finally {
        if (isMounted) {
          setSalespersonLoading(false);
        }
      }
    };

    const debounceTimer = setTimeout(loadSalespersonOptions, 300);

    return () => {
      isMounted = false;
      clearTimeout(debounceTimer);
    };
  }, [apiUserCode, isAgentUser, isSOUser, salespersonSearchText]);

  useEffect(() => {
    let isMounted = true;
    const trimmedSearchText = salespersonL1SearchText.trim();

    const loadSalespersonL1Options = async () => {
      if (!isAgentUser && !isApprovalUser) {
        if (isMounted) {
          setSalespersonL1Options([]);
          setSalespersonL1Loading(false);
        }
        return;
      }

      if (trimmedSearchText && trimmedSearchText.length < 3) {
        if (isMounted) {
          setSalespersonL1Loading(false);
        }
        return;
      }

      setSalespersonL1Loading(true);

      try {
        const salesManagerResponse = await getSalesManagerL1List(trimmedSearchText, apiUserCode);

        if (!isMounted) {
          return;
        }

        setSalespersonL1Options(
          Array.isArray(salesManagerResponse?.userLevelList)
            ? salesManagerResponse.userLevelList.map(buildUserOption).filter((option) => option.value)
            : []
        );
      } catch (error) {
        if (isMounted) {
          setSalespersonL1Options([]);
          showAlert(error?.message || 'Failed to load salesperson L1 dropdown data.');
        }
      } finally {
        if (isMounted) {
          setSalespersonL1Loading(false);
        }
      }
    };

    const debounceTimer = setTimeout(loadSalespersonL1Options, 300);

    return () => {
      isMounted = false;
      clearTimeout(debounceTimer);
    };
  }, [apiUserCode, isAgentUser, isApprovalUser, salespersonL1SearchText]);

  return {
    claims,
    isAgentUser,
    isPrimUser,
    isSOUser,
    isSPUser,
    isApprovalUser,
    loggedInUserCode,
    optionsLoading,
    salespersonCodeOptions,
    salespersonL1Options,
    salespersonLoading,
    salespersonL1Loading,
    salespersonSearchText,
    salespersonL1SearchText,
    setClaims,
    setSalespersonL1SearchText,
    setSalespersonSearchText,
    visibleClaims
  };
};

export default TourExpenseClaimController;
